/**
 * PhotoModal — Ortak fotoğraf paneli (Prompt 5 iter80).
 *
 * Kullanım:
 *   <PhotoModal
 *      open onClose contextType="SHIPMENT_EVIDENCE" refType="shipment" refId={shipmentId}
 *      title="Sevkiyat Fotoğrafları" showTelegram
 *   />
 */
import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Modal, Btn } from "@/components/UI";
import CameraCapture from "@/components/CameraCapture";
import { Camera, Upload, Trash2, Send, Download, X, ChevronLeft, ChevronRight, Loader2, Archive } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const withAuth = (url) => {
  const t = localStorage.getItem("erp_token");
  return `${API}${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}${t ? `&auth=${encodeURIComponent(t)}` : ""}`;
};
// Not: prod'da signed URL kullanılmıyor — endpoint zaten Authorization header ister.
// Bu component'ler <img src>'yi doğrudan yükleyemez. blob URL pattern kullanıyoruz.

async function fetchAsBlob(path) {
  const token = localStorage.getItem("erp_token");
  const r = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return await r.blob();
}

function PhotoTile({ p, onView, onDelete, canDelete }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url;
    fetchAsBlob(`/api/photos/${p.id}/thumb`).then(b => {
      if (cancelled) return;
      url = URL.createObjectURL(b);
      setSrc(url);
    }).catch(() => setSrc(""));
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [p.id]);

  return (
    <div className="relative group rounded overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 aspect-square" data-testid={`photo-tile-${p.id}`}>
      {src === null && <div className="w-full h-full flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-400"/></div>}
      {src && <img src={src} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => onView(p)}/>}
      {canDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(p); }}
          data-testid={`photo-delete-${p.id}`}
          className="absolute top-1 right-1 p-1 rounded bg-rose-500/90 text-white opacity-0 group-hover:opacity-100 transition">
          <Trash2 size={12}/>
        </button>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1 text-[10px] text-white truncate">
        {p.uploaded_by_name}
      </div>
    </div>
  );
}

function Lightbox({ photos, index, onClose, onIndex, watermark }) {
  const p = photos[index];
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    setSrc(null);
    const wm = watermark ? "?watermark=1" : "";
    fetchAsBlob(`/api/photos/${p.id}/file${wm}`).then(b => {
      url = URL.createObjectURL(b);
      setSrc(url);
    }).catch(() => setSrc(""));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [p.id, watermark]);
  const download = async () => {
    const wm = watermark ? "?watermark=1" : "";
    const b = await fetchAsBlob(`/api/photos/${p.id}/file${wm}`);
    const url = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = url; a.download = `${p.id}.jpg`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col" onClick={onClose} data-testid="photo-lightbox">
      <div className="flex items-center justify-between px-4 py-2 text-white text-xs" onClick={e => e.stopPropagation()}>
        <span>{index + 1} / {photos.length}</span>
        <div className="flex gap-2">
          <button onClick={download} className="px-2 py-1 hover:bg-white/10 rounded flex items-center gap-1" data-testid="photo-download"><Download size={14}/> İndir</button>
          <button onClick={onClose} className="px-2 py-1 hover:bg-white/10 rounded"><X size={16}/></button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center relative" onClick={e => e.stopPropagation()}>
        {photos.length > 1 && index > 0 && (
          <button onClick={() => onIndex(index - 1)} className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full"><ChevronLeft size={28}/></button>
        )}
        {photos.length > 1 && index < photos.length - 1 && (
          <button onClick={() => onIndex(index + 1)} className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full"><ChevronRight size={28}/></button>
        )}
        {src === null && <Loader2 className="animate-spin text-white" size={40}/>}
        {src && <img src={src} alt="" className="max-h-[85vh] max-w-[90vw] object-contain"/>}
      </div>
    </div>
  );
}

export default function PhotoModal({
  open, onClose, contextType, refType, refId,
  title = "Fotoğraflar", showTelegram = false, watermark = false,
  onChange,
}) {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [selected, setSelected] = useState({});
  const [cameraOpen, setCameraOpen] = useState(false);

  const load = useCallback(async () => {
    if (!open || !refId) return;
    setBusy(true);
    try {
      const r = await api.get(`/photos?context_type=${contextType}&ref_type=${refType}&ref_id=${refId}`);
      setPhotos(r || []);
    } catch (e) { setPhotos([]); }
    finally { setBusy(false); }
  }, [open, refId, contextType, refType]);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("context_type", contextType);
        fd.append("ref_type", refType);
        fd.append("ref_id", refId);
        const token = localStorage.getItem("erp_token");
        const res = await fetch(`${API}/api/photos`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          alert(err.detail || `Yükleme başarısız: ${res.status}`);
        }
      }
      await load();
      onChange?.();
    } finally { setBusy(false); }
  };

  const onFilePick = (e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; };

  const removePhoto = async (p) => {
    if (!window.confirm("Fotoğraf silinsin mi?")) return;
    try { await api.del(`/photos/${p.id}`); load(); onChange?.(); }
    catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };

  const toggleSel = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));

  const sendTelegram = async () => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    const target = ids.length ? ids : photos.map(p => p.id);
    if (!target.length) { alert("Fotoğraf yok"); return; }
    if (!window.confirm(`${target.length} fotoğraf Telegram ile gönderilsin mi?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/photos/telegram", { photo_ids: target });
      alert(r.ok ? `✓ ${r.sent} fotoğraf gönderildi` : `Gönderilemedi: ${r.response?.error || JSON.stringify(r.response).slice(0, 200)}`);
      setSelected({});
    } catch (e) { alert(e.response?.data?.detail || "Telegram hatası"); }
    finally { setBusy(false); }
  };

  const downloadZip = async () => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    const target = ids.length ? ids : photos.map(p => p.id);
    if (!target.length) { alert("Fotoğraf yok"); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem("erp_token");
      const res = await fetch(`${API}/api/photos/zip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ photo_ids: target, watermark: !!watermark }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || `İndirme başarısız: ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.href = url;
      a.download = `photos-${stamp}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { alert("ZIP indirilemedi: " + (e?.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} title={title} size="xl">
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCameraOpen(true)}
              data-testid="photo-camera-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
            >
              <Camera size={14}/> Kamera ile Çek
            </button>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs cursor-pointer" data-testid="photo-gallery-btn">
              <Upload size={14}/> Galeriden Seç
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={onFilePick}/>
            </label>
            {showTelegram && (
              <Btn size="sm" variant="secondary" onClick={sendTelegram} disabled={busy || photos.length === 0} data-testid="photo-telegram-send">
                <Send size={12}/> Telegramdan Gönder {photos.length > 0 && `(${Object.values(selected).filter(Boolean).length || photos.length})`}
              </Btn>
            )}
            <Btn size="sm" variant="secondary" onClick={downloadZip} disabled={busy || photos.length === 0} data-testid="photo-zip-download">
              <Archive size={12}/> ZIP İndir {photos.length > 0 && `(${Object.values(selected).filter(Boolean).length || photos.length})`}
            </Btn>
            <div className="flex-1"/>
            <span className="text-xs text-slate-500">{photos.length} fotoğraf</span>
          </div>
          {busy && photos.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Yükleniyor...</div>}
          {!busy && photos.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">Henüz fotoğraf yok. Kamera veya galeriden ekleyin.</div>}
          {photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={p.id} className="relative">
                  <label className="absolute top-1 left-1 z-10 bg-white/90 dark:bg-slate-900/90 rounded p-0.5">
                    <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggleSel(p.id)} data-testid={`photo-sel-${p.id}`}/>
                  </label>
                  <PhotoTile p={p} onView={() => setLightboxIdx(i)} onDelete={removePhoto} canDelete={true}/>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
      {lightboxIdx !== null && photos[lightboxIdx] && (
        <Lightbox
          photos={photos} index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onIndex={setLightboxIdx}
          watermark={watermark}
        />
      )}
      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => { setCameraOpen(false); uploadFiles([file]); }}
        title="Sevkiyat Fotoğrafı — Fotoğraf Çek"
      />
    </>
  );
}

/**
 * LabelPhotoStrip — OCR akışları için satır içi küçük thumbnail şeridi (iter94).
 * photoIds: string[] fotoğraf id listesi. Tıklanınca lightbox açılır.
 * Fotoğraflar arka planda `/api/photos/{id}/thumb` + `/api/photos/{id}/file` ile
 * Authorization header'lı çekilir (blob URL); tarayıcı önbelleğini kirletmez.
 */
export function LabelPhotoStrip({ photoIds = [], size = 36, testid }) {
  const [openIdx, setOpenIdx] = useState(null);
  const ids = (photoIds || []).filter(Boolean);
  if (ids.length === 0) return null;
  const asPhotos = ids.map(id => ({ id }));
  return (
    <>
      <div className="inline-flex items-center gap-1" data-testid={testid || "label-photo-strip"}>
        {ids.map((id, i) => (
          <MiniThumb key={id} id={id} size={size} onClick={() => setOpenIdx(i)} testid={`label-thumb-${id}`}/>
        ))}
      </div>
      {openIdx !== null && (
        <Lightbox photos={asPhotos} index={openIdx} onClose={() => setOpenIdx(null)} onIndex={setOpenIdx} watermark={false}/>
      )}
    </>
  );
}

function MiniThumb({ id, size, onClick, testid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url;
    fetchAsBlob(`/api/photos/${id}/thumb`).then(b => {
      if (cancelled) return;
      url = URL.createObjectURL(b);
      setSrc(url);
    }).catch(() => setSrc(""));
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [id]);
  const style = { width: size, height: size };
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className="relative rounded border border-slate-300 dark:border-slate-600 overflow-hidden bg-slate-100 dark:bg-slate-800 hover:ring-2 hover:ring-indigo-400 transition"
      style={style}
      title="Fotoğrafı büyüt"
      data-testid={testid}
    >
      {src === null && <Loader2 size={12} className="animate-spin text-slate-400 absolute inset-0 m-auto"/>}
      {src && <img src={src} alt="" className="w-full h-full object-cover" draggable={false}/>}
    </button>
  );
}

/** Küçük 📷 + sayı butonu — ana tablolar için */
export function PhotoBadgeButton({ count, onClick, testid, title = "Fotoğraflar" }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      title={title}
      data-testid={testid}
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-sky-50 dark:hover:bg-sky-500/10 text-slate-700 dark:text-slate-200"
    >
      <Camera size={12}/> {count > 0 && <span className="tabular-nums font-mono">{count}</span>}
    </button>
  );
}
